/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import BreakdownSites from '../../../shared/BreakdownSites/BreakdownSites';
import { EnergyUsageField } from '../../../shared/Enums';
import {
  convertDataForCarbon,
  getSiteListForCarbon
} from '../../../shared/Utils/portfolioDataFormatter';
import { IMSEMaaSPortfolioFormattedData } from '../IEnergyUsagePortfolioProps';
import { ErrorContainer } from '@bmsp/bmsp.web.component.common';
import { apiNoContentStatusCode } from '../../../shared/Utils/Constants';
import ProgressBarWidget from '../../../shared/ProgressBar/ProgressBarWidget';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import '../PortfolioEnergyUsage/EnergyUsageComponent/EnergyUsageComponent.scss';
import { IEmaaSDataProps } from '../IPortfolioProps';
import { Loader } from '@scuf/common';
import BreakdownSitesForCarbonEnergy from '../../../shared/BreakdownSites/BreakdownSitesForCarbonEnergy';
import { ApiContext } from '../../../../context/ApiContext';
import { ConversionType } from '../../../../main';

export const PortfolioCarbonComponent = (props: IEmaaSDataProps) => {
  const {
    data: { emaasCarbonDataList, carbonErrors },
    visibleSites,
    onShowMore,
    maxRecords
  } = props;

  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [loader, setLoader] = React.useState(true);
  const [
    checkAllsitePreviousData,
    setCheckAllsitePreviousData
  ] = React.useState(true);

  const checkIfAllSitesHasCompleteData = (data: any) => {
    return data && data.every((item: any) => item.site.isCompleted);
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      data &&
      data[0].monthWiseAggregations.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.every(
        (item: any) =>
          item.site.totalActualEmission !== 0 &&
          item.site.totalPreviousPeriodEmission !== 0 &&
          item.site.totalActualEmission !== null &&
          item.site.totalPreviousPeriodEmission !== null
      )
    );
  };

  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data[0].monthWiseAggregations.every(
        (item: {
          totalPreviousPeriodEmission: number | null;
          totalActualEmission: number | null;
        }) =>
          item.totalPreviousPeriodEmission !== 0 &&
          item.totalPreviousPeriodEmission !== null &&
          item.totalActualEmission !== 0 &&
          item.totalActualEmission
      )
    );
  };

  const apiContext = useContext(ApiContext);

  useEffect(() => {
    if (emaasCarbonDataList && emaasCarbonDataList.length > 0) {
      setCheckAllsitePreviousData(
        checkIfAllMonthHasCompleteData(emaasCarbonDataList) &&
          checkIfAllSitesHasPreviousPeriod(emaasCarbonDataList) &&
          checkIfAllMonthsHasPreviousPeriod(emaasCarbonDataList) &&
          checkIfAllSitesHasCompleteData(emaasCarbonDataList)
      );
      setLoader(false);
    }
  }, [emaasCarbonDataList]);

  useEffect(() => {
    if (carbonErrors && carbonErrors !== null) {
      setLoader(false);
    }
  }, [carbonErrors]);

  //Show error based on status code
  if (carbonErrors && carbonErrors.status) {
    if (carbonErrors.status === apiNoContentStatusCode) {
      return (
        <div className="emaas-no-content-error">{t(d.NODATAAVAILABLE)}</div>
      );
    }
    return (
      <div className="emaas-no-content-error">
        <ErrorContainer
          headerText={'Loading Error'}
          errorDescription={
            'Refresh the page to load the data. Contact your administrator if the problem persists.'
          }
        />
      </div>
    );
  }

  let siteEmissionData: IMSEMaaSPortfolioFormattedData = {
    tableData: [],
    progressBarData: undefined,
    displayUnit: ''
  };
  let siteList = getSiteListForCarbon(emaasCarbonDataList!);
  if (siteList && siteList.length > 0) {
    siteEmissionData = convertDataForCarbon(
      siteList,
      emaasCarbonDataList![0].emissionMeasurementUnit,
      visibleSites,
      apiContext.showChangeWhenData
    );
  }

  if (siteEmissionData) {
    const { tableData, progressBarData, displayUnit } = siteEmissionData;
    if (tableData && progressBarData) {
      const {
        actual,
        previousPeriod,
        deviation,
        deviationPercentage,
        actualUnit,
        baselineUnit,
        changeUnit
      } = progressBarData;

      const aggregationCarbon = {
        actual: actual,
        previousPeriod: previousPeriod,
        savings: checkAllsitePreviousData ? deviation! : null,
        savingsPercentage: checkAllsitePreviousData
          ? deviationPercentage!
          : null,
        actualUnit: actualUnit,
        baselineUnit: baselineUnit,
        changeUnit: changeUnit
      };

      const headers = [
        {
          HeaderValue: t(d.SITENAMESHEADER),
          TextAlignValue: 'left',
          Field: 'SiteName',
          initialWidth: '35%'
        },
        {
          HeaderValue: t(d.CARBONNOUNIT),
          TextAlignValue: 'right',
          Field: 'Value',
          sortable: true,
          initialWidth: '25%'
        },
        {
          HeaderValue: t(d.CHANGE),
          TextAlignValue: 'right',
          Field: 'Deviation',
          sortable: true,
          initialWidth: '25%'
        }
      ];

      return loader ? (
        <Loader />
      ) : (
        <div className="kpi-card-flex-container">
          <ProgressBarWidget
            unit={displayUnit}
            category={EnergyUsageField.CARBON}
            aggregationData={aggregationCarbon}
            renderedFromCarbon={true}
            fromCarbonKPI={true}
            originalUnit={emaasCarbonDataList![0].emissionMeasurementUnit}
            conversionType={ConversionType.CARBON}
          />
          <div className="kpi-card-breakdown-site-container">
            <BreakdownSitesForCarbonEnergy
              columnHeaders={headers as IDataTableHeader[]}
              meterData={tableData}
              onShowMore={onShowMore}
              maxRecords={maxRecords}
              activeWidgetName={props.activeWidgetName}
              emissionMeasurementUnit={
                emaasCarbonDataList![0].emissionMeasurementUnit
              }
            />
          </div>
        </div>
      );
    }
  }
  return loader ? (
    <Loader />
  ) : (
    <div className="noDataDisplay ms-common-error-container">
      <ErrorContainer
        headerText={'Loading Error'}
        errorDescription={
          'Refresh the page to load the data. Contact your administrator if the problem persists.'
        }
      />
    </div>
  );
};
export default PortfolioCarbonComponent;
